import { useAuth0 } from "@auth0/auth0-react";
import { Link as RouterLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Alert, AppBar, Box, Button, ButtonBase, Container, Stack, Toolbar, Typography } from "@mui/material";
import { Broadcast, ListChecks, ShieldCheck, SignIn, SignOut } from "@phosphor-icons/react";
import { SessionProvider, useSession } from "./AuthToken";
import Worklist from "./pages/Worklist";
import OrderDetail from "./pages/OrderDetail";
import Live from "./pages/Live";
import Admin from "./pages/Admin";
import EdgeDetail from "./pages/EdgeDetail";
import InstrumentDetail from "./pages/InstrumentDetail";
import SessionView from "./pages/SessionView";
import Join from "./pages/Join";
import { caps, tones } from "./theme";

function Wordmark({ size = 15 }: { size?: number }) {
  return (
    <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ userSelect: "none" }}>
      <Typography component="span" sx={{ ...caps, fontSize: size, fontWeight: 500, letterSpacing: "0.45em", color: "#fff" }}>Jupiter</Typography>
      <Typography component="span" sx={{ ...caps, fontSize: 9, letterSpacing: "0.25em", color: "text.secondary", display: { xs: "none", sm: "inline" } }}>by Moyae</Typography>
    </Stack>
  );
}

function NavLink({ to, label, icon, active }: { to: string; label: string; icon: React.ReactNode; active: boolean }) {
  return (
    <ButtonBase
      component={RouterLink}
      to={to}
      sx={{
        ...caps,
        fontSize: 11,
        letterSpacing: "0.2em",
        height: 64,
        px: { xs: 1.25, sm: 2 },
        gap: 1,
        color: active ? "text.primary" : "text.secondary",
        position: "relative",
        transition: "color 0.2s",
        "&:hover": { color: "text.primary" },
        "& svg": { color: active ? tones.ochre : "inherit" },
        "&::after": {
          content: '""',
          position: "absolute",
          left: 12,
          right: 12,
          bottom: -1,
          height: "1px",
          bgcolor: active ? tones.ochre : "transparent",
          boxShadow: active ? `0 0 10px ${tones.ochre}` : "none",
        },
      }}
    >
      {icon}
      <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{label}</Box>
    </ButtonBase>
  );
}

function Shell() {
  const { logout, user } = useAuth0();
  const { me, error } = useSession();
  const loc = useLocation();
  const nav = [
    { to: "/worklist", label: "Worklist", icon: <ListChecks size={18} /> },
    // remote sessions only exist where an instrument provides live video (for example the Elara with its add-on)
    ...(me?.features?.remoteSessions ? [{ to: "/sessions", label: "Sessions", icon: <Broadcast size={18} /> }] : []),
    ...(me?.role === "admin" ? [{ to: "/admin", label: "Admin", icon: <ShieldCheck size={18} /> }] : []),
  ];
  return (
    <Box>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ minHeight: { xs: 64 }, gap: { xs: 1, md: 3 } }}>
          <Box component={RouterLink} to="/worklist" sx={{ textDecoration: "none", mr: { xs: 1, md: 3 } }}><Wordmark /></Box>
          <Stack direction="row" sx={{ alignSelf: "stretch", alignItems: "center" }}>
            {nav.map((n) => <NavLink key={n.to} {...n} active={loc.pathname.startsWith(n.to)} />)}
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          {me?.tenant && (
            <Typography sx={{ ...caps, fontSize: 10, letterSpacing: "0.2em", color: "text.primary", display: { xs: "none", md: "block" } }}>
              {me.tenant.name}{me.isOps ? <Box component="span" sx={{ color: "primary.main" }}> · Moyae ops</Box> : ""}
            </Typography>
          )}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ display: { xs: "none", lg: "flex" } }}>
            <Typography variant="body2" sx={{ lineHeight: 1 }}>{user?.email}</Typography>
            {me && (
              <Box component="span" sx={{ ...caps, fontSize: 9, lineHeight: 1, color: "text.secondary", border: 1, borderColor: "divider", borderRadius: "999px", px: 0.75, py: 0.5 }}>
                {me.role}
              </Box>
            )}
          </Stack>
          <Button size="small" variant="outlined" color="inherit" startIcon={<SignOut size={16} />} onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>Sign out</Button>
        </Toolbar>
      </AppBar>
      <Container maxWidth="xl" sx={{ py: { xs: 2.5, md: 4 } }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Routes>
          <Route path="/" element={<Navigate to="/worklist" replace />} />
          <Route path="/worklist" element={<Worklist />} />
          <Route path="/orders/:id" element={<OrderDetail />} />
          <Route path="/sessions" element={<Live />} />
          <Route path="/live" element={<Navigate to="/sessions" replace />} />
          <Route path="/sessions/:id" element={<SessionView />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/edges/:id" element={<EdgeDetail />} />
          <Route path="/admin/instruments/:id" element={<InstrumentDetail />} />
          <Route path="*" element={<Navigate to="/worklist" replace />} />
        </Routes>
      </Container>
    </Box>
  );
}

export default function App() {
  // A room phone joins from the QR without a PACS login.
  if (window.location.pathname.startsWith("/j/")) {
    return (
      <Routes>
        <Route path="/j/:pass" element={<Join />} />
      </Routes>
    );
  }
  return <SignedInApp />;
}

function SignedInApp() {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  if (isLoading) return null;
  if (!isAuthenticated) {
    return (
      <Stack alignItems="center" justifyContent="center" spacing={4} sx={{ minHeight: "100vh", px: 2, textAlign: "center" }}>
        <Typography sx={{ ...caps, fontSize: 11, color: "text.secondary" }}>By Moyae</Typography>
        <Typography component="h1" sx={{ ...caps, fontWeight: 200, fontSize: "clamp(40px, 8vw, 84px)", letterSpacing: "0.32em", lineHeight: 1, color: "#fff", mr: "-0.32em" }}>Jupiter</Typography>
        <Button variant="contained" size="large" startIcon={<SignIn size={18} />} onClick={() => loginWithRedirect()} sx={{ px: 4 }}>Sign in</Button>
      </Stack>
    );
  }
  return (
    <SessionProvider>
      <Shell />
    </SessionProvider>
  );
}
